// /////////////////////////////////////////////////////////////
// Router
// /////////////////////////////////////////////////////////////
import React from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from 'framer-motion';
import Login from "../pages/LoginPage";
import PrivateRoute from "./PrivateRoute";
import Home from "../pages/Home";
import ProfitAndLossStatementSales from "../pages/ProfitAndLossStatementSales";
import ProfitAndLossStatementManagement from "../pages/ProfitAndLossStatementManagement";
import SalesProjectsManagement from "../pages/SalesProjectsManagement";
import SalesProjectsCreateNew from "../pages/SalesProjectsCreateNew";
import SalesProjectsDetailPage from "../pages/SalesProjectsDetailPage";
import SalesProjectsLayout from "../pages/SalesProjectsLayout";
import WorkFlow from "../pages/WorkFlow";
import SalesProjectsFilterList from "../pages/SalesProjectsFilterList";
import SfaPipelineDashboard from "../pages/SfaPipelineDashboard";
// 推進案件機能統合 リリース1（案件構造: 仕入契約・販売区画・筆）。最終確認までメニュー未掲載
import SfaProjectsLayout from "../pages/sfa/SfaProjectsLayout";
import SfaProjectListPage from "../pages/sfa/SfaProjectListPage";
import SfaProjectDetailPage from "../pages/sfa/SfaProjectDetailPage";
import SfaProjectCreatePage from "../pages/sfa/SfaProjectCreatePage";
import HozenallNetProjectsManagement from "../pages/HozenallNetProjectsManagement";
import ObcDataSyncManagement from "../pages/ObcDataSyncManagement";
import ObcMasterManagement from "../pages/ObcMasterManagement";
import MonthlyTrialBalanceManagement from "../pages/MonthlyTrialBalanceManagement";
import RecruitmentPlanManagement from "../pages/RecruitmentPlanManagement";
import CashflowManagement from "../pages/CashflowManagement";
import MarketAnalysis from "../pages/MarketAnalysis";
import EventManagement from "../pages/EventManagement";
import EventList from "../pages/EventList";
import MailDeliveryCampaigns from "../pages/MailDeliveryCampaigns";
import MailDeliveryCampaignEditor from "../pages/MailDeliveryCampaignEditor";
import MailDeliveryCampaignDetail from "../pages/MailDeliveryCampaignDetail";
import CrmCustomerManagement from "../pages/CrmCustomerManagement";
import CrmCustomerDetailPage from "../pages/CrmCustomerDetailPage";
import CrmCustomerLayout from "../pages/CrmCustomerLayout";
import BuildingDetailPage from "../pages/BuildingDetailPage";
import AiPlansManagement from "../pages/AiPlansManagement";
import TurquoiseTorihikisakiManagement from "../pages/TurquoiseTorihikisakiManagement";
import TurquoiseMatchingReview from "../pages/TurquoiseMatchingReview";
import GoogleWorkspacePage from "../pages/GoogleWorkspacePage";
import MasterViewManagement from "../pages/MasterViewManagement";
import ToukiManagement from "../pages/ToukiManagement";
import SourceQualityPlatform from "../pages/SourceQualityPlatform";
import IsoDocumentPlatform from "../pages/IsoDocumentPlatform";
import HomeDesignLab from "../pages/HomeDesignLab";

export default function AppRouter() {
  const location = useLocation();

  // 配下 (一覧 ↔ 詳細) で同じ親 Layout を共有するルートは、AnimatePresence の
  // key を共通化して Layout の再マウントを抑止する。これにより一覧フックの
  // 状態 (データ配列・page・検索条件) が保持される。
  const isCrmCustomerRoute =
    location.pathname === '/crm/customers' ||
    location.pathname.startsWith('/crm/customers/');
  const isSalesProjectsRoute =
    location.pathname === '/salesprojectsmanagement' ||
    location.pathname.startsWith('/salesprojectsmanagement/');
  const isSfaProjectsRoute =
    location.pathname === '/sfa/projects' ||
    location.pathname.startsWith('/sfa/projects/');
  const animationKey = isCrmCustomerRoute
    ? '/crm/customers'
    : isSalesProjectsRoute
      ? '/salesprojectsmanagement'
      : isSfaProjectsRoute
        ? '/sfa/projects'
        : location.pathname;

  return (
    // overflow:hidden で Framer Motion トランジション中の縦方向のはみ出し (±10px) を
    // クリップし、遷移時に縦スクロールバーが一瞬出るのを防ぐ (横方向のはみ出しも抑止)。
    // ここに paddingBottom を足すと viewport ちょうどのページで常時スクロールバーが出る
    // ため余白は持たせない。最下部要素の box-shadow がこの overflow / 親の overflow-y:auto
    // にカットされる場合は、各ページ側で最下部に余白 (paddingBottom 等) を設けて影を収める。
    <div style={{ overflow: 'hidden' }}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={animationKey}>
        <Route
          path="/login"
          element={
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <Login />
            </motion.div>
          }
        />

        <Route
          path="/"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Home />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/profitandlossstatement/sales"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ProfitAndLossStatementSales />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/profitandlossstatement/management"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ProfitAndLossStatementManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/salesprojectsmanagement"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <SalesProjectsLayout />
              </motion.div>
            </PrivateRoute>
          }
        >
          <Route index element={<SalesProjectsManagement />} />
          <Route path=":id" element={<SalesProjectsDetailPage />} />
        </Route>

        <Route
          path="/sfapipeline"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <SfaPipelineDashboard />
              </motion.div>
            </PrivateRoute>
          }
        />

        {/* 推進案件機能統合 リリース1: 案件構造の新画面。一覧の状態を保つため、一覧・新規登録・詳細を親 Layout の配下に置く */}
        <Route path="/sfa/projects" element={<PrivateRoute><SfaProjectsLayout /></PrivateRoute>}>
          <Route index element={<SfaProjectListPage />} />
          <Route path="new" element={<SfaProjectCreatePage />} />
          <Route path=":id" element={<SfaProjectDetailPage />} />
        </Route>

        <Route
          path="/salesprojectscreate"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <SalesProjectsCreateNew />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/workflow"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <WorkFlow />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/salesprojectsfilters"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <SalesProjectsFilterList />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/hozenallnetprojectsmanagement"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <HozenallNetProjectsManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/obcdatasync"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ObcDataSyncManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/monthly-trial-balance"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MonthlyTrialBalanceManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/cashflow"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <CashflowManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/recruitment-plan"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <RecruitmentPlanManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/market-analysis"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MarketAnalysis />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/event-management"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <EventList />
              </motion.div>
            </PrivateRoute>
          }
        />

        {/* メール配信 (ブラストメール内製化) */}
        <Route
          path="/mail-delivery"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MailDeliveryCampaigns />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/mail-delivery/campaigns/new"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MailDeliveryCampaignEditor />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/mail-delivery/campaigns/:id/edit"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MailDeliveryCampaignEditor />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/mail-delivery/campaigns/:id"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MailDeliveryCampaignDetail />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/event-management/:eventId"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <EventManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/obc-master-management"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ObcMasterManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/crm/customers"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <CrmCustomerLayout />
              </motion.div>
            </PrivateRoute>
          }
        >
          <Route index element={<CrmCustomerManagement />} />
          {/* 静的セグメント building/:id は 2 セグメントのため 1 セグメントの :id と競合しない */}
          <Route path="building/:id" element={<BuildingDetailPage />} />
          <Route path=":id" element={<CrmCustomerDetailPage />} />
        </Route>

        <Route
          path="/development/ai-plans"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <AiPlansManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/development/source-quality"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <SourceQualityPlatform />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/home2"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <HomeDesignLab />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/development/iso-documents"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <IsoDocumentPlatform />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/turquoise/torihikisaki"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <TurquoiseTorihikisakiManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/turquoise/matching-review"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <TurquoiseMatchingReview />
              </motion.div>
            </PrivateRoute>
          }
        />

        <Route
          path="/google-workspace"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <GoogleWorkspacePage />
              </motion.div>
            </PrivateRoute>
          }
        />

        {/* 登記簿謄本 取込確認 */}
        <Route
          path="/touki"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <ToukiManagement />
              </motion.div>
            </PrivateRoute>
          }
        />

        {/* マスタ管理画面（管理者専用） */}
        <Route
          path="/master/view-management"
          element={
            <PrivateRoute>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <MasterViewManagement />
              </motion.div>
            </PrivateRoute>
          }
        />
        </Routes>
      </AnimatePresence>
    </div>
  );
}